/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { Grid, GridItem, Box, Flex, Text, Spinner } from '@chakra-ui/react';
import {
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Bar,
  BarChart,
  Cell,
} from 'recharts';
import { DailyProfitabilityBarColors } from 'utils/StaticData';

const RentalProfitableGraph = ({ rentalData, isLoading, regionNameMap }) => {
  const getYTicks = (data) => {
    if (!data || data.length === 0) return [0, 1];
    const maxValue = Math.max(...data.map((item) => Number(item.count) || [0]));
    // Calculate optimal tick step based on data range
    const range = maxValue;
    let step;

    if (range <= 10) {
      step = 2;
    } else if (range <= 30) {
      step = 5;
    } else if (range <= 100) {
      step = 10;
    } else if (range <= 200) {
      step = 25;
    } else {
      step = Math.ceil(range / 5 / 50) * 50;
    }

    // Generate ticks
    const ticks = [];
    for (let i = 0; i <= maxValue + step; i += step) {
      ticks.push(i);
    }

    // Ensure we don't show too many ticks
    if (ticks.length > 8) {
      return ticks.filter((_, index) => index % 2 === 0);
    }
    return ticks;
  };

  return (
    <>
      <Grid
        bg="white"
        p={{ base: '4', md: '5' }}
        templateColumns="repeat(12, 1fr)"
        gap={{ base: '16px', lg: '24px' }}
        boxShadow="0px 0px 20px rgba(32, 57, 70, 0.06)"
        borderRadius="20px"
        border="1px solid #D9D9D9"
        w="full"
        mb="20px"
      >
        <GridItem colSpan={{ base: '12', lg: '12' }}>
          <Box w="full" h="full" bg="white">
            <Flex justify="space-between" align="baseline" mb={4}>
              <Text
                fontSize={{ base: '16px', md: '24px' }}
                fontWeight="semibold"
                color="primary"
              >
                Rentals
              </Text>
              {!isLoading && (
                <Flex flexWrap="wrap" align="left" flexDirection="column">
                  {[
                    ...new Map(
                      rentalData?.map((item) => [item.regionId, item]),
                    ).values(),
                  ].map((item, index) => (
                    <Flex
                      key={index}
                      align={{ base: 'flex-start', lg: 'center' }}
                      ml="4"
                    >
                      <Box
                        w={{ base: '8px', lg: '10px' }}
                        h={{ base: '8px', lg: '10px' }}
                        bg={DailyProfitabilityBarColors[item.regionId]}
                        borderRadius="50%"
                        mt={{ base: '4px', lg: '0' }}
                        mr="2"
                        flexShrink="0"
                      />
                      <Text fontSize={{ base: '10px', md: '12px' }}>
                        {regionNameMap[item?.regionId] ||
                          `Region ${item?.regionId}`}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              )}
            </Flex>

            {isLoading ? (
              <>
                <Flex justify="center" alignItems="center" height="300px">
                  <Spinner size="lg" color="black.500" />
                </Flex>
              </>
            ) : rentalData?.length > 0 ? (
              <>
                <Box overflowX="auto" className="custom-scrollbar">
                  <Box minWidth={`${Math.max(rentalData?.length * 50, 600)}px`}>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={rentalData}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: rentalData?.length > 10 ? 70 : 60,
                        }}
                        barCategoryGap={
                          rentalData?.data?.length < 5 ? '10%' : '20%'
                        }
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          horizontal={true}
                          vertical={false}
                        />

                        {/* X-axis: Merchant names */}
                        <XAxis
                          dataKey="merchantName"
                          interval={0}
                          tick={(props) => {
                            const { x, y, payload } = props;
                            const name = payload.value;
                            const maxLength = 10;
                            const truncatedName =
                              name.length > maxLength
                                ? `${name.substring(0, maxLength)}…`
                                : name;

                            return (
                              <text
                                x={x}
                                y={y + 10}
                                textAnchor={
                                  rentalData?.length < 4 ? 'middle' : 'end'
                                }
                                fontSize={12}
                                transform={
                                  rentalData?.length < 4
                                    ? undefined
                                    : `rotate(-60, ${x}, ${y})`
                                }
                              >
                                {truncatedName}
                              </text>
                            );
                          }}
                        />

                        {/* Y-axis: Revenue values */}
                        <YAxis
                          domain={[0]}
                          ticks={getYTicks(rentalData)}
                          tickFormatter={(value) => value}
                          axisLine={false}
                          tickLine={false}
                        />

                        {/* Tooltip */}
                        <Tooltip
                          formatter={(value) => value}
                          labelFormatter={(name) => `Merchant: ${name}`}
                        />

                        {/* Bar with label on top */}
                        <Bar
                          dataKey="count"
                          name="Revenue"
                          radius={[5, 5, 0, 0]}
                        >
                          {rentalData?.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={
                                DailyProfitabilityBarColors[entry?.regionId] ||
                                ''
                              }
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </>
            ) : (
              <Flex justify="center" alignItems="center" height="300px">
                <Text fontSize="18px" fontWeight="bold" color="gray.500">
                  No records available
                </Text>
              </Flex>
            )}
          </Box>
        </GridItem>
      </Grid>
    </>
  );
};
RentalProfitableGraph.propTypes = {
  rentalData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.number,
      region: PropTypes.string,
    }),
  ),
  isLoading: PropTypes.bool,
  regionNameMap: PropTypes.object,
  currency: PropTypes.string,
};

export default RentalProfitableGraph;
